'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslations } from 'next-intl'
import useInfiniteScroll from 'react-infinite-scroll-hook'

import { AudioCard, VoiceIcon } from '@/components/business/voice/shared'
import type { AudioPubListQuery } from '@/lib/api/hooks/audio'
import type { PlazaTrack } from '@/lib/voice/plaza-utils'
import { isQueryLoading, isQuerySettled } from '@/lib/voice/query-loading'

const PROGRESS_UPDATE_INTERVAL_MS = 250

interface PlazaCommunityListProps {
  tracks: PlazaTrack[]
  query: AudioPubListQuery
}

/**
 * 社区音频列表 + 播放器
 * 进度 state 隔离在此组件，避免 Plaza 整页随 onTimeUpdate 重渲染
 */
export const PlazaCommunityList = ({ tracks, query }: PlazaCommunityListProps) => {
  const t = useTranslations('voice.plaza')

  // activeId：当前已加载到 <audio> 的曲目（可能在播放或暂停中）；playing：是否正在播放
  const [activeId, setActiveId] = useState<number | null>(null)
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [durations, setDurations] = useState<Record<number, number>>({})

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const lastProgressUpdateRef = useRef(0)
  const activeIdRef = useRef<number | null>(null)

  const listLoading = isQueryLoading(query)
  const listSettled = isQuerySettled(query)

  const activeTrack = tracks.find(track => track.id === activeId)

  useEffect(() => {
    activeIdRef.current = activeId
  }, [activeId])

  // 仅当「已加载的曲目」变化时才换源并从头播放；同一曲目的暂停/继续不经过此处，从而保留播放位置
  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !activeTrack?.audioUrl) return

    audio.src = activeTrack.audioUrl
    audio.load()
    audio
      .play()
      .then(() => setPlaying(true))
      .catch(() => {
        setActiveId(null)
        setPlaying(false)
        setProgress(0)
      })
  }, [activeId, activeTrack?.audioUrl])

  useEffect(() => {
    const audio = audioRef.current
    return () => {
      audio?.pause()
    }
  }, [])

  // 切换分类（tracks 引用变化）时，若正在播放的 track 已被筛掉，重置播放状态
  useEffect(() => {
    if (activeId === null) return
    const stillExists = tracks.some(track => track.id === activeId)
    if (!stillExists) {
      audioRef.current?.pause()
      setActiveId(null)
      setPlaying(false)
      setProgress(0)
    }
  }, [tracks, activeId])

  const handleTogglePlay = useCallback((trackId: number) => {
    const audio = audioRef.current
    // 同一曲目：在播放 ↔ 暂停间切换，保留当前播放位置（继续时从暂停处开始）
    if (activeIdRef.current === trackId) {
      if (!audio) return
      if (audio.paused) {
        audio.play().then(() => setPlaying(true)).catch(() => undefined)
      } else {
        audio.pause()
        setPlaying(false)
      }
      return
    }
    // 切换到另一首：重置进度并加载新源从头播放（加载/播放由上方 effect 处理）
    setProgress(0)
    setActiveId(trackId)
  }, [])

  // 滚动到底部自动加载更多（桌面/移动端通用），替代手动「Load more」按钮
  const [sentinelRef] = useInfiniteScroll({
    loading: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    onLoadMore: () => query.fetchNextPage(),
    rootMargin: '0px 0px 200px 0px'
  })

  return (
    <>
      <audio
        ref={audioRef}
        className="hidden"
        preload="metadata"
        onEnded={() => {
          setPlaying(false)
          setProgress(0)
          if (audioRef.current) audioRef.current.currentTime = 0
        }}
        onLoadedMetadata={e => {
          const id = activeIdRef.current
          const d = e.currentTarget.duration
          if (id && Number.isFinite(d) && d > 0) {
            setDurations(prev => ({ ...prev, [id]: d }))
          }
        }}
        onTimeUpdate={e => {
          const audio = e.currentTarget
          if (!audio.duration) return
          const now = performance.now()
          if (now - lastProgressUpdateRef.current < PROGRESS_UPDATE_INTERVAL_MS) return
          lastProgressUpdateRef.current = now
          setProgress(audio.currentTime / audio.duration)
        }}
      />

      {listLoading && (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-[13px] text-[var(--text-3)]">
          <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[var(--border-strong)] bg-white/[0.04] text-[var(--text-2)] animate-[voice-spin-slow_2.4s_linear_infinite]">
            <VoiceIcon name="sparkles" size={18} stroke={1.8} />
          </div>
          {t('loadingList')}
        </div>
      )}

      {query.isError && listSettled && (
        <div className="py-16 text-center text-[13px] text-[var(--text-3)]">
          {t('loadFailed')}
        </div>
      )}

      {listSettled && tracks.length === 0 && (
        <div className="py-16 text-center text-[13px] text-[var(--text-3)]">
          {t('emptyList')}
        </div>
      )}

      {tracks.length > 0 && (
        <>
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {tracks.map(track => (
              <AudioCard
                key={track.id}
                durationSec={durations[track.id]}
                isPlaying={activeId === track.id && playing}
                progress={activeId === track.id ? progress : 0}
                track={track}
                onPlay={handleTogglePlay}
              />
            ))}
          </div>

          {query.hasNextPage && (
            <div
              ref={sentinelRef}
              className="mt-5 flex items-center justify-center py-4 text-[12px] text-[var(--text-3)]"
            >
              {query.isFetchingNextPage && (
                <span className="flex items-center gap-2">
                  <span className="flex h-4 w-4 items-center justify-center animate-[voice-spin-slow_1.6s_linear_infinite]">
                    <VoiceIcon name="sparkles" size={14} stroke={1.8} />
                  </span>
                  {t('loadingList')}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </>
  )
}
